import { useState, useEffect } from 'react'
import { Calendar, Clock, CheckCircle2, AlertCircle, ArrowRight, ShieldCheck } from 'lucide-react'
import { fetchCollection } from '../utils/cms'
import SEO from '../components/SEO'

export default function BookingPage() {
  const [treatments, setTreatments] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadTreatments() {
      try {
        const data = await fetchCollection('treatments')
        setTreatments(data)
      } catch (error) {
        console.error('Error loading treatments for booking:', error)
      } finally {
        setLoading(false)
      }
    }
    loadTreatments()
  }, [])

  const steps = [
    {
      num: "1",
      title: "Vælg din behandling",
      description: "Vælg den behandlingsform, der passer bedst til dine symptomer (f.eks. Muskelterapi eller Rørbæk Teknikken)."
    },
    {
      num: "2",
      title: "Vælg dato & tidspunkt",
      description: "Se alle ledige tider direkte i Planway-kalenderen og vælg det tidspunkt, der passer bedst ind i din hverdag."
    },
    {
      num: "3",
      title: "Modtag bekræftelse",
      description: "Så snart du har booket, modtager du en bekræftelse på e-mail og SMS. Vi sender også en påmindelse dagen før."
    }
  ]

  return (
    <div className="space-y-16 pb-16">
      <SEO
        title="Bestil tid online | Rørbækklinikken – Tlf 86 63 93 93"
        description="Book tid online til muskelterapi, kranio-sakral terapi, akupunktur eller massage hos Rørbækklinikken i Viborg, eller ring på 86 63 93 93."
      />
      
      {/* Page Header */}
      <section className="bg-[#F5F0E6]/40 py-16 border-b border-[#E6DFD3]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#1E352F]">
            Online tidsbestilling
          </h1>
          <p className="text-base sm:text-lg text-[#1E352F]/80 leading-relaxed">
            Bestil din næste behandling nemt og hurtigt via vores online bookingsystem Planway. Se alle ledige tider med det samme.
          </p>
        </div>
      </section>

      {/* Booking Steps */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {steps.map((step, idx) => (
            <div
              key={idx}
              className="bg-white rounded-xl p-6 sm:p-8 border border-[#E6DFD3] shadow-sm space-y-4 relative"
            >
              <div className="w-10 h-10 rounded-full bg-[#eebb53] text-white flex items-center justify-center font-bold text-lg">
                {step.num}
              </div>
              <h3 className="font-serif text-lg font-bold text-[#1E352F]">
                {step.title}
              </h3>
              <p className="text-sm text-[#1E352F]/70 leading-relaxed">
                {step.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Main Planway CTA Box */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#1E352F] rounded-2xl text-[#F5F0E6] p-8 sm:p-12 text-center space-y-6 border border-[#2D4A43] shadow-lg relative overflow-hidden">
          <div className="absolute inset-0 opacity-10 pointer-events-none">
            <div className="absolute -top-20 -left-20 w-80 h-80 rounded-full bg-[#eebb53] blur-3xl" />
          </div>

          <div className="space-y-4 relative z-10">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-white text-xs font-semibold uppercase tracking-wider">
              Planway Booking
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white leading-tight">
              Klar til at booke din tid?
            </h2>
            <p className="text-sm sm:text-base text-[#F5F0E6]/80 max-w-xl mx-auto leading-relaxed">
              Klik på knappen nedenfor for at åbne vores eksterne bookingsystem hos Planway i et nyt vindue. Her kan du se priser, varigheder og ledige tider.
            </p>
          </div>

          <div className="pt-4 relative z-10">
            <a
              href="https://roerbaekklinikken.planway.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2.5 bg-[#eebb53] hover:bg-[#dca63d] text-white px-8 py-4 rounded-full text-base font-bold transition-all shadow-md hover:shadow-lg cursor-pointer"
            >
              <Calendar className="w-5 h-5" />
              Gå til booking (Planway)
            </a>
          </div>

          <div className="flex justify-center items-center gap-6 text-xs text-[#F5F0E6]/60 pt-4 border-t border-[#2D4A43] relative z-10">
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-[#eebb53]" />
              Sikker booking
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle2 className="w-4 h-4 text-[#eebb53]" />
              E-mail & SMS bekræftelse
            </span>
          </div>
        </div>
      </section>

      {/* Direct Treatment Booking List */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        <div className="text-center space-y-3">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1E352F]">
            Vælg behandling og bestil
          </h2>
          <p className="text-sm text-[#1E352F]/70">
            Klik på "Bestil behandling" for at gå direkte til den valgte behandlingsform i bookingsystemet.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-[#E6DFD3] divide-y divide-[#F5F0E6] overflow-hidden shadow-sm">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="p-6 h-20 animate-pulse bg-white" />
            ))
          ) : (
            treatments.map((treatment) => (
              <div
                key={treatment.id}
                className="p-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 hover:bg-[#FDFBF7]/50 transition-colors"
              >
                <div className="space-y-1">
                  <h3 className="font-serif text-lg font-bold text-[#1E352F]">
                    {treatment.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-[#1E352F]/60 max-w-xl">
                    {treatment.description}
                  </p>
                </div>
                <div className="flex items-center gap-4 w-full sm:w-auto justify-between sm:justify-end shrink-0">
                  <span className="text-xs sm:text-sm font-semibold text-[#1E352F]/70 flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-[#eebb53]" />
                    {treatment.price_info}
                  </span>
                  <a
                    href="https://roerbaekklinikken.planway.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#1E352F] hover:bg-[#2D4A43] text-white px-5 py-2.5 rounded-full text-xs font-bold transition-colors flex items-center gap-1 shrink-0"
                  >
                    Bestil behandling
                    <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* Cancellation Policy */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-2xl p-6 sm:p-8 border border-[#E6DFD3] shadow-sm flex gap-5 items-start">
          <div className="p-3 rounded-lg bg-amber-50 text-amber-600 shrink-0">
            <AlertCircle className="w-6 h-6" />
          </div>
          <div className="space-y-2">
            <h3 className="font-serif text-lg font-bold text-[#1E352F]">
              Afbudspolitik og praktiske vilkår
            </h3>
            <p className="text-sm text-[#1E352F]/70 leading-relaxed">
              Hvis du bliver forhindret i at møde op til din aftalte tid, beder vi dig venligst melde afbud senest <strong>24 timer før</strong> din behandling. Afbud kan ske online via det link, du modtager i din bekræftelses-e-mail, eller ved at ringe til os på tlf. 86 63 93 93. Ved for sent afbud eller udeblivelse forbeholder vi os retten til at opkræve det fulde gebyr for behandlingen.
            </p>
          </div>
        </div>
      </section>

    </div>
  )
}
